import * as React from 'react'
import { Activity } from 'lucide-react'
import { useEventFeed } from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import { PageHeader } from '@/components/layout/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { sourceLabel } from '@/components/ui/status'
import { DomainDetailDrawer } from '@/components/layout/DomainDetailDrawer'
import { formatDateTime, formatUTC } from '@/lib/utils'

const EVENT_TYPES = [
  { value: '', label: '全部事件' },
  { value: 'status_changed', label: '状态变化' },
  { value: 'availability_confirmed', label: '可注册确认' },
  { value: 'registration_started', label: '进入新注册周期' },
  { value: 'zone_removed', label: 'Zone 未观察到' },
  { value: 'zone_added', label: 'Zone 新增观察' },
  { value: 'date_changed', label: '日期更新' },
]

export default function EventsPage() {
  const [eventType, setEventType] = React.useState('')
  const [detailId, setDetailId] = React.useState<string | null>(null)
  const { data, isLoading, error, refetch } = useEventFeed({
    event_type: eventType || undefined,
    limit: 100,
  })
  const { user } = useAuth()

  return (
    <>
      <PageHeader
        title="生命周期事件"
        description="仅显示与你的监控或已确认命中相关的域名事件。"
        actions={
          <div className="flex flex-wrap gap-1">
            {EVENT_TYPES.map((t) => (
              <Button
                key={t.value}
                variant={eventType === t.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEventType(t.value)}
              >
                {t.label}
              </Button>
            ))}
          </div>
        }
      />

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <LoadingState rows={8} />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyState
          icon={Activity}
          title="暂无事件"
          description="当你监控或命中的域名发生有效状态变化时，会在此形成时间线。"
        />
      ) : (
        <ol className="space-y-2">
          {data!.items.map((e) => (
            <li key={e.id} className="rounded-lg border bg-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={eventVariant(e.event_type)}>{eventLabel(e.event_type)}</Badge>
                <button
                  onClick={() => e.domain_id && setDetailId(e.domain_id)}
                  className="font-mono text-sm text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {e.domain}
                </button>
                {e.from_status && e.to_status && (
                  <span className="text-xs text-muted-foreground">
                    {e.from_status} → {e.to_status}
                  </span>
                )}
                <span
                  className="ml-auto text-xs text-muted-foreground"
                  title={formatUTC(e.observed_at)}
                >
                  {formatDateTime(e.observed_at, user?.timezone)}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                来源 {sourceLabel(e.source)} · 置信度 {e.confidence}/100
                {typeof e.metadata?.note === 'string' && <> · {e.metadata.note}</>}
              </p>
            </li>
          ))}
        </ol>
      )}

      <DomainDetailDrawer domainId={detailId} onClose={() => setDetailId(null)} />
    </>
  )
}

function eventLabel(type: string): string {
  return {
    status_changed: '状态变化', date_changed: '日期更新', registrar_changed: '注册商变化',
    zone_added: 'Zone 新增观察', zone_removed: 'Zone 未观察到',
    registration_started: '进入新注册周期', availability_confirmed: '可注册确认',
    score_changed: '价值分变化', gfw_changed: 'GFW 信号变化',
  }[type] ?? type
}

function eventVariant(type: string) {
  return ({
    availability_confirmed: 'success', registration_started: 'warning',
    status_changed: 'default', zone_removed: 'muted', zone_added: 'muted',
  } as const)[type] ?? 'outline'
}
